import { useState } from 'react';
import { api, ApiError, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { GazetteIssue, GazetteNoticeCategory, LegalNoticeSubmission, LegalNoticeSubmissionStatus, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge, Tabs } from '../../ui/components';

const NOTICE_CATEGORIES: GazetteNoticeCategory[] = [
  'ACT_COMMENCEMENT',
  'LEGAL_NOTICE',
  'GENERAL_NOTICE',
  'APPOINTMENT',
  'STATUTORY_INSTRUMENT',
  'CORRIGENDUM',
];

const TABS = [
  { key: 'submit', label: 'Submit a notice' },
  { key: 'queue', label: 'Review queue' },
  { key: 'lookup', label: 'Look up by id' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

/**
 * MDA-submitted gazette notices with fee — POST /legal-notice-submissions is
 * open to any authenticated T1+ officer (not just AG Chambers staff);
 * GET (queue) + approve/reject/publish are GAZETTE_EDITOR only. There is no
 * "my submissions" list endpoint, so a non-editor submitter can only look
 * their own notice up by the id the submit response gave them.
 */
export function LegalNotices() {
  const { persona } = useAuth();
  const isEditor = persona?.roles.includes('GAZETTE_EDITOR') ?? false;
  const [tab, setTab] = useState<TabKey>(isEditor ? 'queue' : 'submit');

  return (
    <div>
      <PageHeader title="Legal Notice Submissions" subtitle="MDA-submitted Gazette notices, with fee where applicable." />
      <Tabs tabs={TABS} value={tab} onChange={setTab} />
      {tab === 'submit' ? <SubmitPanel /> : null}
      {tab === 'queue' ? <QueuePanel isEditor={isEditor} /> : null}
      {tab === 'lookup' ? <LookupPanel /> : null}
    </div>
  );
}

function SubmitPanel() {
  const { persona } = useAuth();
  const [submittingMda, setSubmittingMda] = useState(persona?.org ?? '');
  const [noticeType, setNoticeType] = useState<GazetteNoticeCategory>('LEGAL_NOTICE');
  const [title, setTitle] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [created, setCreated] = useState<LegalNoticeSubmission | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<LegalNoticeSubmission>('/api/v1/m38/legal-notice-submissions', {
        submittingMda,
        noticeType,
        title,
        bodyText,
      });
      setCreated(res);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Submission failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-xl">
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {created ? (
        <div className="card p-5">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-bold text-slate-900">Submitted</span>
            <StatusBadge status={created.status} />
          </div>
          <p className="text-xs text-slate-500">
            Reference id <code>{created.id}</code> — save this to look it up later.
          </p>
          {created.feeAmount ? <p className="mt-1 text-xs text-slate-500">Fee: {scr(created.feeAmount)}</p> : null}
          <button
            className="btn-secondary mt-4"
            onClick={() => {
              setCreated(null);
              setTitle('');
              setBodyText('');
            }}
          >
            Submit another
          </button>
        </div>
      ) : (
        <div className="card space-y-3 p-5">
          <Field label="Submitting MDA">
            <input className="field-input" value={submittingMda} onChange={(e) => setSubmittingMda(e.target.value)} />
          </Field>
          <Field label="Notice category">
            <select className="field-input" value={noticeType} onChange={(e) => setNoticeType(e.target.value as GazetteNoticeCategory)}>
              {NOTICE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Title">
            <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
          <Field label="Body text">
            <textarea className="field-input" rows={5} value={bodyText} onChange={(e) => setBodyText(e.target.value)} />
          </Field>
          <button
            className="btn-primary w-full"
            onClick={submit}
            disabled={busy || !submittingMda.trim() || title.trim().length < 3 || !bodyText.trim()}
          >
            {busy ? 'Submitting…' : 'Submit notice'}
          </button>
        </div>
      )}
    </div>
  );
}

function QueuePanel({ isEditor }: { isEditor: boolean }) {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<LegalNoticeSubmissionStatus | ''>('');
  const [selected, setSelected] = useState<LegalNoticeSubmission | null>(null);

  const params = new URLSearchParams({ limit: '50' });
  if (q.trim()) params.set('q', q.trim());
  if (status) params.set('status', status);
  const listQ = useApiQuery<Page<LegalNoticeSubmission>>(isEditor ? `/api/v1/m38/legal-notice-submissions?${params.toString()}` : null);

  if (!isEditor) return <ErrorNote message="Requires GAZETTE_EDITOR — the review queue is not visible to this persona." />;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Title / MDA contains" className="min-w-[220px] flex-1">
          <input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} />
        </Field>
        <Field label="Status" className="!mb-0">
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value as LegalNoticeSubmissionStatus | '')}>
            <option value="">All</option>
            {(['SUBMITTED', 'FEE_PENDING', 'APPROVED', 'REJECTED', 'PUBLISHED'] as LegalNoticeSubmissionStatus[]).map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="Nothing in the queue" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Title</th>
                <th className="table-head">MDA</th>
                <th className="table-head">Category</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((n) => (
                <tr key={n.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelected(n)}>
                  <td className="table-cell font-medium">{n.title}</td>
                  <td className="table-cell text-xs">{n.submittingMda}</td>
                  <td className="table-cell text-xs">{n.noticeType.replaceAll('_', ' ')}</td>
                  <td className="table-cell">
                    <StatusBadge status={n.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected ? (
        <NoticeDetailModal
          submission={selected}
          onClose={() => setSelected(null)}
          onDone={() => {
            setSelected(null);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function NoticeDetailModal({
  submission,
  onClose,
  onDone,
}: {
  submission: LegalNoticeSubmission;
  onClose: () => void;
  onDone: () => void;
}) {
  const compilingQ = useApiQuery<Page<GazetteIssue>>('/api/v1/m38/gazette-issues?status=COMPILING&limit=50');
  const [gazetteIssueId, setGazetteIssueId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const act = async (action: 'approve' | 'reject' | 'publish', body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m38/legal-notice-submissions/${submission.id}/${action}`, body);
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : `${action} failed`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={submission.title} onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-500">{submission.submittingMda}</span>
          <StatusBadge status={submission.status} />
        </div>
        <p className="whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-sm text-slate-700">{submission.bodyText}</p>
        {submission.rejectionReason ? <ErrorNote message={`Rejected: ${submission.rejectionReason}`} /> : null}

        {submission.status === 'SUBMITTED' ? (
          <div className="flex flex-wrap gap-2">
            <button className="btn-success" onClick={() => act('approve')} disabled={busy}>
              Approve
            </button>
          </div>
        ) : null}
        {submission.status === 'SUBMITTED' || submission.status === 'APPROVED' ? (
          <div className="flex flex-wrap items-end gap-2">
            <Field label="Rejection reason" className="min-w-[220px] flex-1">
              <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
            <button className="btn-danger" onClick={() => act('reject', { reason })} disabled={busy || reason.trim().length < 3}>
              Reject
            </button>
          </div>
        ) : null}
        {submission.status === 'APPROVED' ? (
          <div className="flex flex-wrap items-end gap-2 border-t border-slate-100 pt-3">
            <Field label="Publish into gazette issue (must be COMPILING)" className="min-w-[220px] flex-1">
              <select className="field-input" value={gazetteIssueId} onChange={(e) => setGazetteIssueId(e.target.value)}>
                <option value="">Select…</option>
                {compilingQ.data?.items.map((g) => (
                  <option key={g.id} value={g.id}>
                    No. {g.issueNumber} of {g.issueYear}
                  </option>
                ))}
              </select>
            </Field>
            <button className="btn-primary" onClick={() => act('publish', { gazetteIssueId })} disabled={busy || !gazetteIssueId}>
              Publish
            </button>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

function LookupPanel() {
  const [id, setId] = useState('');
  const [result, setResult] = useState<LegalNoticeSubmission | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const lookup = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<LegalNoticeSubmission>(`/api/v1/m38/legal-notice-submissions/${id.trim()}`);
      setResult(res);
    } catch (e) {
      setResult(null);
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl">
      <div className="mb-3 flex gap-2">
        <input className="field-input" value={id} onChange={(e) => setId(e.target.value)} placeholder="Submission id…" />
        <button className="btn-primary shrink-0" onClick={lookup} disabled={loading || !id.trim()}>
          {loading ? 'Looking up…' : 'Look up'}
        </button>
      </div>
      {error ? <ErrorNote message={error} /> : null}
      {result ? (
        <div className="card p-5">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-bold text-slate-900">{result.title}</span>
            <StatusBadge status={result.status} />
          </div>
          <p className="text-xs text-slate-500">{result.submittingMda}</p>
          <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{result.bodyText}</p>
          {result.rejectionReason ? <div className="mt-2 text-xs text-rose-600">Rejected: {result.rejectionReason}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
